import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Plus, Trash2, Edit3, Check, X, FileDown, Loader2, Filter,
    Clock3, BookMarked, User as UserIcon,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";

const UNITES = ["U4", "U51", "U52", "U61", "U62"];
const FILIERES = [
    { value: "electrotechnique", label: "BTS Électrotechnique" },
    { value: "ciel", label: "BTS CIEL" },
];

const FILIERE_LABEL = {
    electrotechnique: "BTS Électrotechnique",
    ciel: "BTS CIEL",
};

function currentSchoolYear() {
    const now = new Date();
    return now.getMonth() >= 8 ? `${now.getFullYear()}-${now.getFullYear() + 1}` : `${now.getFullYear() - 1}-${now.getFullYear()}`;
}

const EMPTY_ROW = () => ({
    school_year: currentSchoolYear(),
    filiere: "electrotechnique",
    classe: "",
    unite: "U4",
    hours: 0,
    themes: "",
    formateur_id: "",
    formateur_name: "",
});

export default function FormateurTracking() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [formateurs, setFormateurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(EMPTY_ROW());
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    const [filterYear, setFilterYear] = useState("all");
    const [filterFiliere, setFilterFiliere] = useState("all");
    const [filterFormateur, setFilterFormateur] = useState("all");

    const canEdit = user && ["formateur", "responsable", "admin"].includes(user.role);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rowsRes, fmtRes] = await Promise.all([
                api.get("/formateur-tracking"),
                api.get("/formateur-tracking/formateurs"),
            ]);
            setRows(rowsRes.data || []);
            setFormateurs(fmtRes.data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial data fetch on mount.
    useEffect(() => { load(); }, [load]);

    // No useEffect pre-fill — the formateur dropdown defaults to the current
    // user at submit time (see addRow).

    const yearOptions = useMemo(() => {
        const set = new Set(rows.map((r) => r.school_year).filter(Boolean));
        set.add(currentSchoolYear());
        return Array.from(set).sort().reverse();
    }, [rows]);

    const filtered = useMemo(() => {
        return rows.filter((r) =>
            (filterYear === "all" || r.school_year === filterYear) &&
            (filterFiliere === "all" || r.filiere === filterFiliere) &&
            (filterFormateur === "all" || r.formateur_id === filterFormateur)
        );
    }, [rows, filterYear, filterFiliere, filterFormateur]);

    const totalHours = filtered.reduce((s, r) => s + (Number(r.hours) || 0), 0);

    // Group by formateur for the summary block & PDF
    const byFormateur = useMemo(() => {
        const map = new Map();
        filtered.forEach((r) => {
            const key = r.formateur_id || r.formateur_name || "—";
            if (!map.has(key)) {
                map.set(key, { name: r.formateur_name || "—", total: 0, rows: [] });
            }
            const node = map.get(key);
            node.total += Number(r.hours) || 0;
            node.rows.push(r);
        });
        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [filtered]);

    const addRow = async () => {
        if (!draft.classe.trim()) {
            toast.error("Renseignez une classe / BTS.");
            return;
        }
        // Default formateur to current user when not explicitly chosen.
        const payload = { ...draft };
        if (!payload.formateur_id && user) {
            payload.formateur_id = user.id;
            payload.formateur_name = `${user.prenom || ""} ${user.nom || ""}`.trim() || user.email;
        }
        try {
            const { data } = await api.post("/formateur-tracking", payload);
            setRows((r) => [data, ...r]);
            setDraft((d) => ({
                ...EMPTY_ROW(),
                formateur_id: d.formateur_id,
                formateur_name: d.formateur_name,
                school_year: d.school_year,
                filiere: d.filiere,
                classe: d.classe,
            }));
            toast.success("Ligne ajoutée");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const startEdit = (row) => {
        setEditingId(row.id);
        setEditDraft({ ...row });
    };

    const saveEdit = async () => {
        if (!editDraft) return;
        try {
            const { data } = await api.put(`/formateur-tracking/${editingId}`, editDraft);
            setRows((rs) => rs.map((r) => (r.id === editingId ? data : r)));
            setEditingId(null);
            setEditDraft(null);
            toast.success("Modifications enregistrées");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const removeRow = async (id) => {
        if (!window.confirm("Supprimer cette ligne ?")) return;
        try {
            await api.delete(`/formateur-tracking/${id}`);
            setRows((rs) => rs.filter((r) => r.id !== id));
            toast.success("Ligne supprimée");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const exportPdf = () => {
        // Generate a print-friendly HTML in a new tab via Blob URL (safe, no document.write)
        const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
        const today = new Date().toLocaleDateString("fr-FR");
        const filtersLabel = [
            filterYear !== "all" && `Année ${filterYear}`,
            filterFiliere !== "all" && (FILIERE_LABEL[filterFiliere] || filterFiliere),
            filterFormateur !== "all" && (formateurs.find((f) => f.id === filterFormateur)?.name || ""),
        ].filter(Boolean).join(" · ") || "Tous formateurs · Toutes années · Tous BTS";

        const rowsHtml = filtered.map((r) => `
            <tr>
              <td>${esc(r.school_year)}</td>
              <td>${esc(r.formateur_name)}</td>
              <td>${esc(FILIERE_LABEL[r.filiere] || r.filiere)}</td>
              <td>${esc(r.classe)}</td>
              <td>${esc(r.unite)}</td>
              <td style="text-align:right">${Number(r.hours).toFixed(1)} h</td>
              <td>${esc(r.themes)}</td>
            </tr>
        `).join("");

        const summaryHtml = byFormateur.map((g) => `
            <tr>
              <td>${esc(g.name)}</td>
              <td style="text-align:right"><strong>${g.total.toFixed(1)} h</strong></td>
              <td style="text-align:right">${g.rows.length} ligne${g.rows.length > 1 ? "s" : ""}</td>
            </tr>
        `).join("");

        const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8" />
<title>Suivi annuel des formateurs · Profawz</title>
<style>
  @page { size: A4 landscape; margin: 1.5cm; }
  body { font-family: -apple-system, "Segoe UI", Arial, sans-serif; color: #0a0a0a; }
  header { border-bottom: 2px solid #0a0a0a; padding-bottom: 12px; margin-bottom: 18px; }
  h1 { margin: 0; font-size: 22px; }
  .meta { font-size: 11px; color: #6B7280; letter-spacing: .12em; text-transform: uppercase; }
  .filters { font-size: 12px; color: #6B7280; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 8px; page-break-inside: auto; }
  th, td { border: 1px solid #d1d5db; padding: 6px 8px; vertical-align: top; }
  th { background: #f3f4f6; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .05em; }
  tr { page-break-inside: avoid; }
  .total-row { background: #15A36A; color: #fff; font-weight: bold; }
  h2 { font-size: 14px; margin: 24px 0 8px; border-left: 4px solid #15A36A; padding-left: 8px; }
  .print-btn { position: fixed; top: 12px; right: 12px; padding: 10px 18px; background: #15A36A; color: #fff; border: none; cursor: pointer; font-weight: bold; }
  @media print { .print-btn { display: none; } }
</style>
</head><body>
<button class="print-btn" onclick="window.print()">Imprimer / Enregistrer en PDF</button>
<header>
  <div class="meta">Profawz · Suivi annuel des formateurs</div>
  <h1>Récapitulatif des heures réalisées</h1>
  <div class="filters">${esc(filtersLabel)} — Édité le ${today}</div>
</header>

<h2>Synthèse par formateur</h2>
<table>
  <thead><tr><th>Formateur</th><th style="text-align:right">Total heures</th><th style="text-align:right">Interventions</th></tr></thead>
  <tbody>
    ${summaryHtml || '<tr><td colspan="3" style="text-align:center;color:#6B7280">Aucune donnée</td></tr>'}
    <tr class="total-row"><td>TOTAL GÉNÉRAL</td><td style="text-align:right">${totalHours.toFixed(1)} h</td><td style="text-align:right">${filtered.length}</td></tr>
  </tbody>
</table>

<h2>Détail des interventions</h2>
<table>
  <thead><tr>
    <th>Année</th><th>Formateur</th><th>BTS</th><th>Classe</th><th>Unité</th><th style="text-align:right">Heures</th><th>Thèmes abordés</th>
  </tr></thead>
  <tbody>
    ${rowsHtml || '<tr><td colspan="7" style="text-align:center;color:#6B7280">Aucune ligne</td></tr>'}
  </tbody>
</table>
</body></html>`;
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank", "noopener,noreferrer");
        if (!w) {
            URL.revokeObjectURL(url);
            toast.error("Veuillez autoriser les pop-ups pour exporter le PDF.");
            return;
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    };

    return (
        <div className="min-h-screen bg-paper" data-testid="formateur-tracking-page">
            <Navbar />
            <main className="max-w-7xl mx-auto px-4 py-8">
                <Link to="/dashboard" className="text-xs font-mono text-muted2 hover:text-orange inline-flex items-center gap-1 mb-3">
                    <ArrowLeft size={12} /> Tableau de bord
                </Link>
                <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
                    <div>
                        <div className="label-eyebrow">Tableau collaboratif</div>
                        <h1 className="h-display text-3xl sm:text-4xl font-semibold">Suivi annuel des formateurs</h1>
                        <p className="text-sm text-muted2 mt-1 max-w-2xl">
                            Renseignez les heures effectuées, thèmes abordés et unités du référentiel pour chaque intervention.
                            Tous les membres de l&apos;établissement peuvent ajouter et modifier les lignes.
                        </p>
                    </div>
                    <button onClick={exportPdf} className="btn-primary !text-xs !px-4 !py-2 inline-flex" data-testid="tracking-export-pdf">
                        <FileDown size={14} /> Télécharger en PDF
                    </button>
                </div>

                {/* SUMMARY CARDS */}
                <div className="grid sm:grid-cols-3 gap-3 my-6">
                    <div className="panel p-4">
                        <div className="label-eyebrow flex items-center gap-1"><Clock3 size={12} /> Total heures filtrées</div>
                        <div className="h-display text-3xl font-bold text-orange mt-1" data-testid="tracking-total-hours">{totalHours.toFixed(1)} h</div>
                    </div>
                    <div className="panel p-4">
                        <div className="label-eyebrow flex items-center gap-1"><BookMarked size={12} /> Interventions</div>
                        <div className="h-display text-3xl font-bold mt-1" data-testid="tracking-total-rows">{filtered.length}</div>
                    </div>
                    <div className="panel p-4">
                        <div className="label-eyebrow flex items-center gap-1"><UserIcon size={12} /> Formateurs concernés</div>
                        <div className="h-display text-3xl font-bold mt-1" data-testid="tracking-total-formateurs">{byFormateur.length}</div>
                    </div>
                </div>

                {/* FILTERS */}
                <div className="panel p-3 bg-surface flex flex-wrap items-center gap-3 mb-4" data-testid="tracking-filters">
                    <div className="flex items-center gap-1.5 text-xs font-mono text-muted2">
                        <Filter size={13} /> Filtrer :
                    </div>
                    <div className="flex items-center gap-1.5">
                        <label className="text-[11px] font-mono text-muted2">Année</label>
                        <select className="field-input !py-1 !text-xs" value={filterYear} onChange={(e) => setFilterYear(e.target.value)} data-testid="tracking-filter-year">
                            <option value="all">Toutes</option>
                            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <label className="text-[11px] font-mono text-muted2">BTS</label>
                        <select className="field-input !py-1 !text-xs" value={filterFiliere} onChange={(e) => setFilterFiliere(e.target.value)} data-testid="tracking-filter-filiere">
                            <option value="all">Tous</option>
                            {FILIERES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <label className="text-[11px] font-mono text-muted2">Formateur</label>
                        <select className="field-input !py-1 !text-xs" value={filterFormateur} onChange={(e) => setFilterFormateur(e.target.value)} data-testid="tracking-filter-formateur">
                            <option value="all">Tous</option>
                            {formateurs.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                    </div>
                    {(filterYear !== "all" || filterFiliere !== "all" || filterFormateur !== "all") && (
                        <button onClick={() => { setFilterYear("all"); setFilterFiliere("all"); setFilterFormateur("all"); }} className="text-[11px] text-orange hover:underline" data-testid="tracking-filter-reset">
                            Réinitialiser
                        </button>
                    )}
                </div>

                {/* ADD ROW */}
                {canEdit && (
                    <div className="panel p-4 mb-4 bg-emerald-50/40 border-emerald-200" data-testid="tracking-add-row">
                        <div className="label-eyebrow mb-3">Ajouter une intervention</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 items-end">
                            <div className="col-span-2 sm:col-span-1">
                                <label className="text-[10px] font-mono text-muted2 block">Année</label>
                                <input className="field-input !py-1 !text-xs w-full" value={draft.school_year} onChange={(e) => setDraft({ ...draft, school_year: e.target.value })} placeholder="2025-2026" data-testid="tracking-new-year" />
                            </div>
                            <div className="col-span-2 sm:col-span-1">
                                <label className="text-[10px] font-mono text-muted2 block">Formateur</label>
                                <select className="field-input !py-1 !text-xs w-full" value={draft.formateur_id} onChange={(e) => {
                                    const f = formateurs.find((x) => x.id === e.target.value);
                                    setDraft({ ...draft, formateur_id: e.target.value, formateur_name: f?.name || "" });
                                }} data-testid="tracking-new-formateur">
                                    <option value="">— Choisir —</option>
                                    {formateurs.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-mono text-muted2 block">BTS</label>
                                <select className="field-input !py-1 !text-xs w-full" value={draft.filiere} onChange={(e) => setDraft({ ...draft, filiere: e.target.value })} data-testid="tracking-new-filiere">
                                    {FILIERES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-mono text-muted2 block">Classe</label>
                                <input className="field-input !py-1 !text-xs w-full" value={draft.classe} onChange={(e) => setDraft({ ...draft, classe: e.target.value })} placeholder="ex. 1ère A" data-testid="tracking-new-classe" />
                            </div>
                            <div>
                                <label className="text-[10px] font-mono text-muted2 block">Unité</label>
                                <select className="field-input !py-1 !text-xs w-full" value={draft.unite} onChange={(e) => setDraft({ ...draft, unite: e.target.value })} data-testid="tracking-new-unite">
                                    {UNITES.map((u) => <option key={u} value={u}>{u}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-mono text-muted2 block">Heures</label>
                                <input type="number" min="0" step="0.5" className="field-input !py-1 !text-xs w-full" value={draft.hours} onChange={(e) => setDraft({ ...draft, hours: parseFloat(e.target.value) || 0 })} data-testid="tracking-new-hours" />
                            </div>
                            <div className="col-span-2 sm:col-span-2 lg:col-span-2">
                                <label className="text-[10px] font-mono text-muted2 block">Thèmes abordés</label>
                                <input className="field-input !py-1 !text-xs w-full" value={draft.themes} onChange={(e) => setDraft({ ...draft, themes: e.target.value })} placeholder="ex. Lois de Kirchhoff, TP atelier" data-testid="tracking-new-themes" />
                            </div>
                        </div>
                        <button onClick={addRow} className="btn-primary !text-xs !px-4 !py-2 mt-3 inline-flex" data-testid="tracking-add-button">
                            <Plus size={13} /> Ajouter
                        </button>
                    </div>
                )}

                {/* TABLE */}
                <div className="panel overflow-x-auto" data-testid="tracking-table-wrapper">
                    <table className="w-full text-sm" data-testid="tracking-table">
                        <thead className="bg-ink text-white">
                            <tr>
                                <th className="text-left px-3 py-2 text-[11px] font-mono tracking-wider">Année</th>
                                <th className="text-left px-3 py-2 text-[11px] font-mono tracking-wider">Formateur</th>
                                <th className="text-left px-3 py-2 text-[11px] font-mono tracking-wider">BTS</th>
                                <th className="text-left px-3 py-2 text-[11px] font-mono tracking-wider">Classe</th>
                                <th className="text-left px-3 py-2 text-[11px] font-mono tracking-wider">Unité</th>
                                <th className="text-right px-3 py-2 text-[11px] font-mono tracking-wider">Heures</th>
                                <th className="text-left px-3 py-2 text-[11px] font-mono tracking-wider">Thèmes</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan="8" className="text-center py-10 text-muted2"><Loader2 size={18} className="animate-spin inline-block mr-2 text-orange" />Chargement…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan="8" className="text-center py-10 text-muted2 text-sm" data-testid="tracking-empty">
                                    Aucune intervention enregistrée{filterYear !== "all" || filterFiliere !== "all" || filterFormateur !== "all" ? " pour ces filtres" : ""}.
                                </td></tr>
                            )}
                            {filtered.map((r) => {
                                const isEditing = editingId === r.id;
                                if (isEditing) {
                                    return (
                                        <tr key={r.id} className="border-t border-rule bg-orange/5" data-testid={`tracking-row-edit-${r.id}`}>
                                            <td className="px-3 py-2"><input className="field-input !py-1 !text-xs w-20" value={editDraft.school_year} onChange={(e) => setEditDraft({ ...editDraft, school_year: e.target.value })} /></td>
                                            <td className="px-3 py-2">
                                                <select className="field-input !py-1 !text-xs" value={editDraft.formateur_id} onChange={(e) => {
                                                    const f = formateurs.find((x) => x.id === e.target.value);
                                                    setEditDraft({ ...editDraft, formateur_id: e.target.value, formateur_name: f?.name || editDraft.formateur_name });
                                                }}>
                                                    {formateurs.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                                                </select>
                                            </td>
                                            <td className="px-3 py-2">
                                                <select className="field-input !py-1 !text-xs" value={editDraft.filiere} onChange={(e) => setEditDraft({ ...editDraft, filiere: e.target.value })}>
                                                    {FILIERES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                                </select>
                                            </td>
                                            <td className="px-3 py-2"><input className="field-input !py-1 !text-xs w-24" value={editDraft.classe} onChange={(e) => setEditDraft({ ...editDraft, classe: e.target.value })} /></td>
                                            <td className="px-3 py-2">
                                                <select className="field-input !py-1 !text-xs" value={editDraft.unite} onChange={(e) => setEditDraft({ ...editDraft, unite: e.target.value })}>
                                                    {UNITES.map((u) => <option key={u} value={u}>{u}</option>)}
                                                </select>
                                            </td>
                                            <td className="px-3 py-2 text-right"><input type="number" min="0" step="0.5" className="field-input !py-1 !text-xs w-16 text-right" value={editDraft.hours} onChange={(e) => setEditDraft({ ...editDraft, hours: parseFloat(e.target.value) || 0 })} /></td>
                                            <td className="px-3 py-2"><input className="field-input !py-1 !text-xs w-full" value={editDraft.themes} onChange={(e) => setEditDraft({ ...editDraft, themes: e.target.value })} /></td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                <button onClick={saveEdit} className="text-emerald-700 hover:bg-emerald-50 p-1" title="Enregistrer" data-testid={`tracking-save-${r.id}`}><Check size={14} /></button>
                                                <button onClick={() => { setEditingId(null); setEditDraft(null); }} className="text-muted2 hover:bg-rule p-1 ml-1" title="Annuler"><X size={14} /></button>
                                            </td>
                                        </tr>
                                    );
                                }
                                return (
                                    <tr key={r.id} className="border-t border-rule hover:bg-orange/5" data-testid={`tracking-row-${r.id}`}>
                                        <td className="px-3 py-2 font-mono text-xs">{r.school_year}</td>
                                        <td className="px-3 py-2">{r.formateur_name}</td>
                                        <td className="px-3 py-2 text-xs">{FILIERE_LABEL[r.filiere] || r.filiere}</td>
                                        <td className="px-3 py-2">{r.classe}</td>
                                        <td className="px-3 py-2"><span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 !px-1.5 !py-0 text-[10px]">{r.unite}</span></td>
                                        <td className="px-3 py-2 text-right font-mono">{Number(r.hours).toFixed(1)} h</td>
                                        <td className="px-3 py-2 text-xs max-w-md truncate" title={r.themes}>{r.themes || "—"}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            {canEdit && (
                                                <>
                                                    <button onClick={() => startEdit(r)} className="text-orange hover:bg-orange/10 p-1" title="Modifier" data-testid={`tracking-edit-${r.id}`}><Edit3 size={13} /></button>
                                                    <button onClick={() => removeRow(r.id)} className="text-red-600 hover:bg-red-50 p-1 ml-1" title="Supprimer" data-testid={`tracking-delete-${r.id}`}><Trash2 size={13} /></button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length > 0 && (
                                <tr className="border-t-2 border-ink bg-ink text-white font-bold" data-testid="tracking-total-row">
                                    <td colSpan="5" className="px-3 py-3 text-right">TOTAL</td>
                                    <td className="px-3 py-3 text-right font-mono">{totalHours.toFixed(1)} h</td>
                                    <td colSpan="2" className="px-3 py-3 text-xs">{filtered.length} intervention{filtered.length > 1 ? "s" : ""}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </main>
            <Footer />
        </div>
    );
}
